import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  FormErrorMessage,
  Heading,
  Text,
  VStack,
  Container,
  InputGroup,
  InputRightElement,
  IconButton,
  Alert,
  AlertIcon,
  useToast,
  Link,
} from '@chakra-ui/react';
import { EyeIcon, EyeOffIcon, LogInIcon, ArrowLeftIcon } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import apiClient from '../service/ApiClient';
import LanguageSwitcher from '../i18n/LanguageSwitcher';
import TurnstileWidget, { getCaptchaErrorDetail, CAPTCHA_DISABLED } from './common/TurnstileWidget';

const Login = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    username: '',
    password: '',
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState('');

  // Forgot / Reset password state
  const [mode, setMode] = useState('login'); // 'login' | 'forgot' | 'reset'
  const [resetEmail, setResetEmail] = useState('');
  const [resetUid, setResetUid] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [forgotSuccess, setForgotSuccess] = useState(false);
  const [resetError, setResetError] = useState('');

  // Cloudflare Turnstile token. The backend refuses these forms without a
  // valid token, so submit stays disabled until the widget yields one.
  // Only one form is rendered at a time, so one token/ref serves all modes.
  // Emailed one-time code stage (managers and up, once per 24h). Set when
  // the server answers a correct password with otp_required.
  const [otpStage, setOtpStage] = useState(null); // {challenge, emailHint}
  const [otpCode, setOtpCode] = useState('');

  const [captchaToken, setCaptchaToken] = useState('');
  const turnstileRef = useRef(null);
  const resetCaptcha = () => {
    setCaptchaToken('');
    if (turnstileRef.current) turnstileRef.current.reset();
  };

  const navigate = useNavigate();
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();

  // Each mode mounts a fresh widget; drop any token from the previous one.
  useEffect(() => {
    setCaptchaToken('');
  }, [mode]);

  // Check if already authenticated or if URL has reset params
  useEffect(() => {
    const uid = searchParams.get('reset_uid');
    const token = searchParams.get('reset_token');
    if (uid && token) {
      setResetUid(uid);
      setResetToken(token);
      setMode('reset');
      // Clean up URL params
      setSearchParams({});
      return;
    }

    if (apiClient.isAuthenticated()) {
      // ?next= returns the user to the page that sent them here (e.g. the
      // AI-connector consent page). Same-origin paths only: "/x" yes,
      // "//evil.com" or "https://…" no.
      const next = searchParams.get('next') || '';
      const safeNext = next.startsWith('/') && !next.startsWith('//')
        && !next.startsWith('/\\');
      navigate(safeNext ? next : '/dashboard', { replace: true });
    }
  }, [navigate, searchParams, setSearchParams]);

  // Form validation
  const validate = () => {
    const newErrors = {};

    if (!formData.username.trim()) {
      newErrors.username = t('auth.login.errors.missingFields');
    }

    if (!formData.password) {
      newErrors.password = t('auth.login.errors.missingFields');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }

    // Clear login error when typing
    if (loginError) {
      setLoginError('');
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setIsLoading(true);

    try {
      // Call the login method from the API client
      const response = await apiClient.login(
        formData.username, formData.password, captchaToken,
        otpStage ? { code: otpCode.trim(), challenge: otpStage.challenge } : undefined
      );

      if (response && response.otpRequired) {
        // Correct password; a code is on its way. The captcha token was
        // consumed by that request, so arm a fresh one for the code submit.
        setOtpStage({ challenge: response.challenge, emailHint: response.emailHint });
        setOtpCode('');
        setLoginError('');
        resetCaptcha();
        setIsLoading(false);
        return;
      }

      toast({
        title: t('ui:login.toasts.loginSuccess'),
        description: t('ui:login.toasts.welcome'),
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      // Redirect to dashboard
      window.location.reload();
    } catch (error) {
      console.error('Login failed:', error);
      // Turnstile tokens are single-use — get a fresh one for the retry.
      resetCaptcha();
      const code = error.response?.data?.code;
      if (code === 'otp_invalid') {
        setLoginError(t('auth.login.otpWrong', 'That code is not right.'));
      } else if (code === 'otp_expired') {
        setOtpStage(null);
        setOtpCode('');
        setLoginError(t('auth.login.otpExpired', 'That code expired. Sign in again to get a new one.'));
      } else if (code === 'otp_email_missing') {
        setLoginError(t('auth.login.otpNoEmail', 'This account requires a sign-in code but has no email address on file. Ask your hotel administrator to add one.'));
      } else if (code === 'otp_throttled') {
        setLoginError(t('auth.login.otpThrottled', 'Too many sign-in codes requested. Try again in 15 minutes.'));
      } else {
        setLoginError(
          getCaptchaErrorDetail(error) ||
          error.message || t('ui:login.errors.loginFailed')
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Handle forgot password submission
  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    if (!resetEmail.trim()) {
      setResetError(t('ui:login.errors.identifierRequired'));
      return;
    }
    setIsLoading(true);
    setResetError('');
    try {
      await apiClient.requestPasswordReset(resetEmail, captchaToken);
      setForgotSuccess(true);
    } catch (error) {
      resetCaptcha();
      setResetError(getCaptchaErrorDetail(error) || t('ui:login.errors.generic'));
    } finally {
      setIsLoading(false);
    }
  };

  // Handle password reset confirmation
  const handleResetSubmit = async (e) => {
    e.preventDefault();
    setResetError('');

    if (!newPassword || !confirmNewPassword) {
      setResetError(t('ui:login.errors.bothFieldsRequired'));
      return;
    }
    if (newPassword.length < 8) {
      setResetError(t('ui:login.errors.passwordTooShort'));
      return;
    }
    if (newPassword !== confirmNewPassword) {
      setResetError(t('ui:login.errors.passwordsMismatch'));
      return;
    }

    setIsLoading(true);
    try {
      await apiClient.confirmPasswordReset(resetUid, resetToken, newPassword, confirmNewPassword, captchaToken);
      toast({
        title: t('ui:login.toasts.resetSuccess'),
        description: t('ui:login.toasts.resetSuccessDetail'),
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      setMode('login');
      setNewPassword('');
      setConfirmNewPassword('');
    } catch (error) {
      resetCaptcha();
      const msg = getCaptchaErrorDetail(error) || error.response?.data?.error || t('ui:login.errors.resetLinkInvalid');
      setResetError(msg);
    } finally {
      setIsLoading(false);
    }
  };

  const switchToLogin = () => {
    setMode('login');
    setResetError('');
    setForgotSuccess(false);
    setResetEmail('');
  };

  return (
    <Box
      minH="100vh"
      display="flex"
      alignItems="center"
      justifyContent="center"
      bgGradient="linear(to-br, blue.50, white, blue.50)"
    >
      <Container maxW="md" p={8} bg="white" borderRadius="lg" boxShadow="lg" position="relative">
        {/* Language switcher floats top-right of the login card so a guest
            user can pick their language before signing in. Hidden when
            the deployment supports only one language. */}
        <Box position="absolute" top={3} right={3}>
          <LanguageSwitcher size="xs" variant="ghost" />
        </Box>
        <VStack spacing={6} align="stretch">
          <Box textAlign="center" mb={2}>
            <Heading size="lg" mb={2}>{t('app.name', 'FrontDesko')}</Heading>
            <Text color="gray.500">
              {mode === 'login' && t('auth.login.subtitle')}
              {mode === 'forgot' && t('ui:login.subtitles.forgot')}
              {mode === 'reset' && t('ui:login.subtitles.reset')}
            </Text>
          </Box>

          {/* LOGIN MODE */}
          {mode === 'login' && (
            <>
              {loginError && (
                <Alert status="error" borderRadius="md">
                  <AlertIcon />
                  {loginError}
                </Alert>
              )}

              <form onSubmit={handleSubmit}>
                <VStack spacing={4}>
                  <FormControl isInvalid={errors.username} isRequired>
                    <FormLabel>{t('auth.login.username')}</FormLabel>
                    <Input
                      name="username"
                      value={formData.username}
                      onChange={handleChange}
                      placeholder={t('auth.login.usernamePlaceholder')}
                      autoComplete="username"
                    />
                    <FormErrorMessage>{errors.username}</FormErrorMessage>
                  </FormControl>

                  <FormControl isInvalid={errors.password} isRequired>
                    <FormLabel>{t('auth.login.password')}</FormLabel>
                    <InputGroup>
                      <Input
                        name="password"
                        type={showPassword ? 'text' : 'password'}
                        value={formData.password}
                        onChange={handleChange}
                        placeholder={t('auth.login.passwordPlaceholder')}
                        autoComplete="current-password"
                      />
                      <InputRightElement>
                        <IconButton
                          size="sm"
                          variant="ghost"
                          aria-label={showPassword ? t('ui:login.aria.hidePassword') : t('ui:login.aria.showPassword')}
                          icon={showPassword ? <EyeOffIcon size={18} /> : <EyeIcon size={18} />}
                          onClick={() => setShowPassword(!showPassword)}
                        />
                      </InputRightElement>
                    </InputGroup>
                    <FormErrorMessage>{errors.password}</FormErrorMessage>
                  </FormControl>

                  {otpStage && (
                    <FormControl isRequired>
                      <FormLabel>{t('auth.login.otpLabel', 'Sign-in code')}</FormLabel>
                      <Input
                        name="otp_code"
                        value={otpCode}
                        onChange={(e) => setOtpCode(e.target.value)}
                        placeholder="123456"
                        inputMode="numeric"
                        maxLength={6}
                        autoComplete="one-time-code"
                        autoFocus
                        fontSize="xl"
                        letterSpacing="6px"
                        textAlign="center"
                      />
                      <Text fontSize="sm" color="gray.500" mt={2}>
                        <Trans
                          i18nKey="ui:login.otp.codeSent"
                          values={{ email: otpStage.emailHint || t('ui:login.otp.yourEmail') }}
                          components={{ b: <b /> }}
                        />{' '}
                        <Link color="blue.500" onClick={() => { setOtpStage(null); setOtpCode(''); resetCaptcha(); }}>
                          {t('auth.login.otpStartOver', 'Start over')}
                        </Link>
                      </Text>
                    </FormControl>
                  )}

                  <TurnstileWidget ref={turnstileRef} onToken={setCaptchaToken} mt={2} />

                  <Button
                    type="submit"
                    colorScheme="blue"
                    size="lg"
                    width="full"
                    mt={4}
                    isLoading={isLoading}
                    isDisabled={(!captchaToken && !CAPTCHA_DISABLED) || (otpStage && otpCode.trim().length < 6)}
                    leftIcon={<LogInIcon size={20} />}
                  >
                    {otpStage ? t('auth.login.otpSubmit', 'Verify code') : t('auth.login.submit')}
                  </Button>
                </VStack>
              </form>

              <VStack spacing={3}>
                <Text textAlign="center" fontSize="sm" color="gray.500">
                  {t('auth.login.forgotPassword')}{' '}
                  <Link color="blue.500" onClick={() => setMode('forgot')} cursor="pointer">
                    {t('ui:login.links.resetItHere')}
                  </Link>
                </Text>
              </VStack>
            </>
          )}

          {/* FORGOT PASSWORD MODE */}
          {mode === 'forgot' && (
            <>
              {resetError && (
                <Alert status="error" borderRadius="md">
                  <AlertIcon />
                  {resetError}
                </Alert>
              )}

              {forgotSuccess ? (
                <Alert status="success" borderRadius="md">
                  <AlertIcon />
                  {t('ui:login.forgot.successMessage')}
                </Alert>
              ) : (
                <form onSubmit={handleForgotSubmit}>
                  <VStack spacing={4}>
                    <FormControl isRequired>
                      <FormLabel>{t('ui:login.forgot.identifierLabel')}</FormLabel>
                      <Input
                        value={resetEmail}
                        onChange={(e) => { setResetEmail(e.target.value); setResetError(''); }}
                        placeholder={t('ui:login.forgot.identifierPlaceholder')}
                        autoComplete="username"
                      />
                    </FormControl>

                    <TurnstileWidget ref={turnstileRef} onToken={setCaptchaToken} />

                    <Button
                      type="submit"
                      colorScheme="blue"
                      size="lg"
                      width="full"
                      mt={2}
                      isLoading={isLoading}
                      isDisabled={!captchaToken && !CAPTCHA_DISABLED}
                    >
                      {t('ui:login.buttons.sendResetLink')}
                    </Button>
                  </VStack>
                </form>
              )}

              <VStack spacing={3}>
                <Link
                  color="blue.500"
                  fontSize="sm"
                  onClick={switchToLogin}
                  cursor="pointer"
                  display="flex"
                  alignItems="center"
                  gap={1}
                >
                  <ArrowLeftIcon size={14} /> {t('ui:login.links.backToLogin')}
                </Link>
              </VStack>
            </>
          )}

          {/* RESET PASSWORD MODE */}
          {mode === 'reset' && (
            <>
              {resetError && (
                <Alert status="error" borderRadius="md">
                  <AlertIcon />
                  {resetError}
                </Alert>
              )}

              <form onSubmit={handleResetSubmit}>
                <VStack spacing={4}>
                  <FormControl isRequired>
                    <FormLabel>{t('ui:login.reset.newPasswordLabel')}</FormLabel>
                    <InputGroup>
                      <Input
                        type={showNewPassword ? 'text' : 'password'}
                        value={newPassword}
                        onChange={(e) => { setNewPassword(e.target.value); setResetError(''); }}
                        placeholder={t('ui:login.reset.newPasswordPlaceholder')}
                        autoComplete="new-password"
                      />
                      <InputRightElement>
                        <IconButton
                          size="sm"
                          variant="ghost"
                          aria-label={showNewPassword ? t('ui:login.aria.hide') : t('ui:login.aria.show')}
                          icon={showNewPassword ? <EyeOffIcon size={18} /> : <EyeIcon size={18} />}
                          onClick={() => setShowNewPassword(!showNewPassword)}
                        />
                      </InputRightElement>
                    </InputGroup>
                  </FormControl>

                  <FormControl isRequired>
                    <FormLabel>{t('ui:login.reset.confirmPasswordLabel')}</FormLabel>
                    <InputGroup>
                      <Input
                        type={showConfirmPassword ? 'text' : 'password'}
                        value={confirmNewPassword}
                        onChange={(e) => { setConfirmNewPassword(e.target.value); setResetError(''); }}
                        placeholder={t('ui:login.reset.confirmPasswordPlaceholder')}
                        autoComplete="new-password"
                      />
                      <InputRightElement>
                        <IconButton
                          size="sm"
                          variant="ghost"
                          aria-label={showConfirmPassword ? t('ui:login.aria.hide') : t('ui:login.aria.show')}
                          icon={showConfirmPassword ? <EyeOffIcon size={18} /> : <EyeIcon size={18} />}
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        />
                      </InputRightElement>
                    </InputGroup>
                  </FormControl>

                  <TurnstileWidget ref={turnstileRef} onToken={setCaptchaToken} />

                  <Button
                    type="submit"
                    colorScheme="blue"
                    size="lg"
                    width="full"
                    mt={2}
                    isLoading={isLoading}
                    isDisabled={!captchaToken && !CAPTCHA_DISABLED}
                  >
                    {t('ui:login.buttons.resetPassword')}
                  </Button>
                </VStack>
              </form>

              <VStack spacing={3}>
                <Link
                  color="blue.500"
                  fontSize="sm"
                  onClick={switchToLogin}
                  cursor="pointer"
                  display="flex"
                  alignItems="center"
                  gap={1}
                >
                  <ArrowLeftIcon size={14} /> {t('ui:login.links.backToLogin')}
                </Link>
              </VStack>
            </>
          )}
        </VStack>
      </Container>
    </Box>
  );
};

export default Login;
